<template>
  <div class="h-100 wrapper">
    <div    >
      <b-row class="heading-container">
        <go-back />
        <h4 class="ml-2">
          {{ $t("add_fee") | startcase }}
        </h4>
      </b-row>
      <div class="mb-3">
        <span>{{ currentProgress }}% {{ $t("complete") }}</span>
        <b-progress :value="currentProgress" :max="100" variant="success"/>
      </div>
    </div>

    <validation-observer ref="observer" tag="fieldset" v-slot="{ invalid }">
      <!-- Type of fee -->
      <b-row class="mt-3">
        <b-col>
          <distribution-form-group
            vid="feeType"
            ref="feeType"
            :label="$t('type_of_fee') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
          >
            <b-form-select required v-model="fee.type" :options="feeOptions" :state="state"></b-form-select>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Effective Calendar Start -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.INTERNATIONAL || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="effectiveStart"
            ref="effectiveStart"
            :label="$t('effective_calendar_start') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.effectiveStart"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Effective Calendar End -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.INTERNATIONAL || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="effectiveEnd"
            ref="effectiveEnd"
            :label="$t('effective_calendar_end') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.effectiveEnd"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Territory of royalties -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.INTERNATIONAL || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="country"
            ref="country"
            :label="$t('territory_of_incoming_royalties') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-form-select required v-model="fee.country" :options="countriesOptions" :state="state"></b-form-select>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Tariff -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.BASE || fee.type === feeStatuses.SERVICE || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="tariffs"
            ref="tariffs"
            :label="$t('tariff') | capitalize"
            label-cols="2"
            :rules="fee.tariffs.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="include-exclude-append">
              <template v-slot:append>
                <include-exclude-selector
                  :options="tariffOptions"
                  :except-string="$t('all_except') | capitalize"
                  :include-string="$t('include_specific') | capitalize"
                  :placeholder="getPlaceholder('tariffs')"
                  label=""
                  label-cols="0"
                  v-model="fee.tariffs"
                  :enable-all="true"
                  selectorCols="col-3 pr-0"
                />
              </template>
              <b-form-select required v-model="fee.tariffs.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Period Start -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.BASE || fee.type === feeStatuses.SERVICE || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="periodStart"
            ref="periodStart"
            :label="$t('period_start') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.periodStart"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Period End -->
      <b-row class="mt-2" v-if="fee.type === feeStatuses.BASE || fee.type === feeStatuses.SERVICE || fee.type === null">
        <b-col>
          <distribution-form-group
            vid="periodEnd"
            ref="periodEnd"
            :label="$t('period_end') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.periodEnd"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Rightsholder -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="rightsholder"
            ref="rightsholder"
            :label="$t('rightsholder') | capitalize"
            label-cols="2"
            :rules="fee.rightsholder.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="custom-select-search">
              <template v-slot:append>
                <b-form-select class="col-3" v-model="fee.rightsholder.except">
                  <b-form-select-option value="all">{{ $t("all") | capitalize }}</b-form-select-option>
                  <b-form-select-option :value="false">{{ $t('include_specific') | capitalize }}</b-form-select-option>
                  <b-form-select-option :value="true">{{ $t('all_except') | capitalize }}</b-form-select-option>
                </b-form-select>

                <multi-select 
                  v-model="fee.rightsholder.values" 
                  id="societyMultiSelectSearch"
                  label="name" 
                  track-by="value" 
                  :placeholder="getPlaceholder('rightsholder')" 
                  :options="rightsholdersOptions" 
                  :multiple="true" 
                  :searchable="true" 
                  :loading="isLoading"
                  :close-on-select="false" 
                  :show-no-results="true" 
                  @search-change="findRightsholders"
                  :disabled="fee.rightsholder.except === 'all'"
                >
                </multi-select>
              </template>
              <b-form-select required v-model="fee.rightsholder.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Society -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="societies"
            ref="societies"
            :label="$t('society') | capitalize"
            label-cols="2"
            :rules="fee.societies.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="include-exclude-append">
              <template v-slot:append>
                <include-exclude-selector
                  :options="memberCollectivesOptions"
                  :except-string="$t('all_except') | capitalize"
                  :include-string="$t('include_specific') | capitalize"
                  :placeholder="getPlaceholder('societies')"
                  label=""
                  label-cols="0"
                  v-model="fee.societies"
                  :enable-all="true"
                  selectorCols="col-3 pr-0"
                />
              </template>
              <b-form-select required v-model="fee.societies.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Rate -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="rate"
            ref="rate"
            :label="$t('rate') | capitalize"
            label-cols="2"
            rules="double|required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-form-input required v-model="fee.rate" :state="state"></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>
      <div class="fixed-footer" ref="footer">
        <div class="d-flex justify-content-end">
          <b-button class="btn-outline-dark" @click="cancel">{{ $t("cancel") | capitalize }}</b-button>
          <b-button
            class="ml-3 btn-outline-red"
            @click="submit"
            :disabled="invalid"
            :title="(!invalid ? '' : $t('fill_in_the_missing_fields')) | capitalize"
          >
            {{ $t("create") | capitalize }}
          </b-button>
        </div>
      </div>
    </validation-observer>
    <modal
      id="cancel-modal"
      cancel-variant="outline-primary"
      ok-variant="outline-secondary"
      :cancel-title="$t('no_continue_editing') | capitalize"
      :ok-title="$t('yes_cancel_editing') | capitalize"
      @ok="realCancel()"
      @cancel="$bvModal.hide('cancel-modal')"
      modal-type="info"
      centered
      :custom-title="$t('create_fee') | capitalize"
    >
      {{ $t("cancel_create_fee") }}
    </modal>
  </div>
</template>
<script>
import { capitalize, debounce } from "lodash"
import { mapActions, mapState } from "vuex"
import DistributionFormGroup from "@/components/DistributionFormGroup"
import { ValidationObserver } from "vee-validate"
import { feeStatuses } from "@/constants"

export default {
  name: "CreateFeesPage",
  components: { DistributionFormGroup, ValidationObserver },
  data () {
    return {
      fee: {
        type: null,
        effectiveStart: null,
        effectiveEnd: null,
        country: null,
        tariffs: {
          values: [],
          except: null,
        },
        rightsholder: {
          values: [],
          except: null,
        },
        societies: {
          values: [],
          except: null,
        },
        rate: null,
        periodStart: null,
        periodEnd: null,
      },
      totalFieldsCount: 0,
      failedFieldsCount: 0,
      isLoading: false,
      rightsholdersOptions: [],
      feeStatuses,
      feeOptions: [
        { value: feeStatuses.BASE, text: capitalize(this.$t("base_fees")) },
        { value: feeStatuses.SERVICE, text: capitalize(this.$t("service_fees")) },
        { value: feeStatuses.INTERNATIONAL, text: capitalize(this.$t("international_incoming")) },
      ],
    }
  },
  computed: {
    ...mapState("consts", ["countries", "tariffs", "memberCollectives" ]),
    errors () {
      return this.$refs.observer.errors
    },
    requiredFields () {
      let fields = ["feeType", "rate", "rightsholder", "societies"]
      if (this.fee.type === feeStatuses.INTERNATIONAL) {
        fields = fields.filter(field => !["periodStart", "periodEnd"].includes(field))
        fields.push("effectiveStart", "effectiveEnd", "country")
      }
      if (this.fee.type === feeStatuses.BASE || this.fee.type === feeStatuses.SERVICE) {
        fields = fields.filter(field => !["effectiveStart", "effectiveEnd", "country"].includes(field))
        fields.push("periodStart", "periodEnd")
      }
      return fields
    },
    currentProgress () {
      if (this.totalFieldsCount > 0)
        return Math.ceil((this.totalFieldsCount - this.failedFieldsCount) / this.totalFieldsCount * 100)
      else
        return 0
    },
    countriesOptions () {
      let options = this.countries.map(c => {
        return { text: c.text, value: c.alpha3 }
      })
      return options
    },
    tariffOptions () {
      let options = this.tariffs.map(c => {
        return { name: c.text, value: c.code }
      })
      return options
    },
    memberCollectivesOptions () {
      let options = this.memberCollectives.map(c => {
        return { name: c.name, value: c.uuid }
      })
      return options
    },
  },
  methods: {
    ...mapActions("consts", ["getTariffs", "getCountries"]),
    ...mapActions("alert", ["success", "error"]),
    updateProgress () {
      let errors = this.errors
      let fields = Object.entries(errors)
      let failedOptionalFields = fields.filter(([k,]) => !this.requiredFields.includes(k)).length
      this.totalFieldsCount = this.requiredFields.length + failedOptionalFields
      this.failedFieldsCount = fields.filter(([,v]) => v.length).length
    },
    async validate () {
      return await this.$refs.observer.validate()
    },
    getPlaceholder (key) {
      if (this.fee[key]?.select === "all")
        return ""
      return this.fee[key]?.select ? this.$t("except_placeholder") : this.$t("include_placeholder")
    },
    findRightsholders (query) {
      if (query) {
        this.isLoading = true
        this.$api.rightsholders.rightsholdersList({ search: query }).then(response => {
          this.rightsholdersOptions = response.data.results
            .map(r => {
              return {
                "name": r["name"],
                "value": r["resoundId"],
                "id": r.id,
                "resoundId": r.resoundId,
              }
            })
        }).finally(() => {
          this.isLoading = false
        })
      }
    },
    cancel () {
      this.$bvModal.show("cancel-modal")
    },
    realCancel () {
      this.success(this.$t("fee_creation_cancelled"))
      this.$router.go(-1)
    },
    submit () {
      let data = {
        type: this.fee.type,
        rate: this.fee.rate,
        period: {
          start: this.fee.type === feeStatuses.INTERNATIONAL ? this.fee.effectiveStart : this.fee.periodStart,
          end: this.fee.type === feeStatuses.INTERNATIONAL ? this.fee.effectiveEnd : this.fee.periodEnd,
        },
        societies: {
          ids: this.fee.societies.values.map(society => society.value),
          select: this.getSelectVal(this.fee.societies.except),
        },
        rightsHolders: {
          ids: this.fee.rightsholder.values.map(rightsholder => rightsholder.id),
          select: this.getSelectVal(this.fee.rightsholder.except),
        },
        tariffs: {
          codes: this.fee.tariffs.values.map(tariff => tariff.value),
          select: this.getSelectVal(this.fee.tariffs.except),
        },
        country: this.fee.country,
      }
      this.$api.fees.createFee(data).then(() => {
        this.success(capitalize(this.$t("created_successfully")))
        this.$router.go(-1)
      }).catch(error => {
        const { data, status } = error.response
        let message = ""
        if (status === 400) {
          message = capitalize(this.$t("form_contains_errors"))
          this.$refs.observer.setErrors(data)
        } else if (status === 422) {
          message = "Failed to update payout. Please correct the data and try again."
          if (data.nonFieldErrors) {
            message = message + "\n" + data.nonFieldErrors.map(e => e.message).join("\n")
          }
        } else {
          message = "Failed to update payout. Please contact administrator or try again."
        }
        this.error(message)
      }) 
    },
    getSelectVal (value) {
      switch (value) {
      case "all": return "all"
      case false: return "include"
      case true: return "exclude"
      default: return null
      }
    },
  },
  watch: {
    "fee.type" (newVal, oldVal) {
      if (oldVal === null && newVal) {
        this.fee.tariffs.except = "all"
        this.fee.rightsholder.except = "all"
        this.fee.societies.except = "all"
      }
      if (newVal === feeStatuses.INTERNATIONAL) {
        this.fee.tariffs.values = []
      } else if (newVal === feeStatuses.BASE || newVal === feeStatuses.SERVICE) {
        this.fee.country = null
      }
    },
    "fee.tariffs.except" (newVal) {
      if (newVal === "all") {
        this.fee.tariffs.values = []
      }
    },
    "fee.rightsholder.except" (newVal) {
      if (newVal === "all") {
        this.fee.rightsholder.values = []
      }
    },
    "fee.societies.except" (newVal) {
      if (newVal === "all") {
        this.fee.societies.values = []
      }
    },
  },
  mounted () {
    this.$watch("errors", () => {
      this.updateProgress()
    })
    if (!this.tariffs.length) {
      this.getTariffs()
    }
    if (!this.countries.length) {
      this.getCountries()
    }
  }, 
  created () {
    this.findRightsholders = debounce(this.findRightsholders, 500)
  }
}
</script>

<style lang="scss" scoped>
.custom-select-search {
  .input-group-append {
    width: stretch;
    column-gap: 20px;
    .custom-select {
      border-radius: 0.25rem;
      background-color: white;
    }
    .select-search-input {
      padding: 0;
    }
    ::v-deep .input-group {
      padding-right: 20px;
    }
    ::v-deep .form-control[readonly] {
      background-color: transparent;
    }
    ::v-deep .form-control[disabled] {
      background-color: transparent;
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: all;
    }
  }
}

.include-exclude-append {
  width: stretch;
  .input-group-append {
    width: stretch;
    .includeExcludeSelector {
      width: stretch;
      column-gap: 20px;
    }
    ::v-deep .custom-select {
      border-radius: 0.25rem;
      background-color: white;
    }
  }
}

::v-deep .multiselect--disabled {
  cursor: not-allowed;
  pointer-events: all;
}

::v-deep .multiselect--disabled .multiselect__select {
    background-color: white !important;
    cursor: not-allowed;
  }

::v-deep fieldset[disabled] {
  cursor: not-allowed;
}

.wrapper {
    padding-bottom: 30px;
}

.fixed-footer {
  position: fixed;
  height: 30px;
  bottom: 40px;
  left: 0;
  width: calc(100% - 15px);
}
</style>